import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { IncomingMessage } from 'node:http';

import { dmaUserParams } from '@/config/dma/paramConfig';
import useLocation, {
    prefetchLocation,
    type PrefetchLocationOptions,
} from '@/entities/location/hooks/useLocation';
import { Coordinates } from '@/entities/location/utils/configs/location';
import { getLocalPlaybackMvpdId } from '@/entities/video/utils/isEssentialSubscriber';
import { graphql } from '@/graphql/__generated/gql';
import { DmaParams, FullUser } from '@/graphql/__generated/gql/graphql';
import useFullUser from '@/hooks/providers/useFullUser';
import { getParamsFromObject } from '@/utils/getParamsFromObject';
import { prefetchRequest, useRequest } from '@/utils/request';
import type { ssrState } from '@/utils/server/prefetch-utils';

const cacheName = 'dma-request';
interface UseDmasOptions {
    enabled?: boolean;
    staleTime?: number;
    cacheTime?: number;
    refetchOnWindowFocus?: boolean;
}

interface UseDmasParams {
    coordinates?: Coordinates;
    isLTSEnabled?: boolean;
    locationEnabled?: boolean;
    options?: UseDmasOptions;
}

const query = graphql(/* GraphQL */ `
    query dmaInfo($params: DmaParams!) {
        dmaInfo(params: $params) {
            dmas {
                baseChannelId
                callSign
                callSignId
                dma
                dmaName
                postalCode
                stationId
                tokenDetails {
                    playback_url
                    syncBackAuth_url
                    syncBackToken
                    tokenExpirationTime
                }
            }
            success
            debugInfo {
                longitude
                latitude
                userState
                ipAddress
            }
        }
    }
`);

// ~16 km at mid-latitudes — below this, assume same DMA region
const COORDINATES_CHANGE_THRESHOLD_DEGREES = 0.145;

function coordinatesChanged(prev: Coordinates, current: Coordinates): boolean {
    return (
        Math.abs(prev.latitude - current.latitude) > COORDINATES_CHANGE_THRESHOLD_DEGREES ||
        Math.abs(prev.longitude - current.longitude) > COORDINATES_CHANGE_THRESHOLD_DEGREES
    );
}

// The dmaInfo GraphQL contract requires all four parameters as non-null strings.
// Anonymous or unhydrated profiles leave them undefined, causing BAD_USER_INPUT errors.
const REQUIRED_DMA_PARAM_KEYS = [
    'userState',
    'packageCode',
    'userProfileType',
    'userRegistrationCountry',
] as const;

export const hasRequiredDmaParams = (
    userFullProfile?: FullUser | null,
): userFullProfile is FullUser => {
    if (!userFullProfile) return false;
    const params = getParamsFromObject(userFullProfile, dmaUserParams) as Record<string, unknown>;
    return REQUIRED_DMA_PARAM_KEYS.every((key) => {
        const value = params[key];
        return typeof value === 'string' && value.length > 0;
    });
};

const useDmas = ({
    coordinates,
    isLTSEnabled = false,
    locationEnabled,
    options = {},
}: UseDmasParams = {}) => {
    const { userFullProfile, isFetched: isProfileFetched } = useFullUser();
    const { location } = useLocation(false, locationEnabled !== false);
    const queryClient = useQueryClient();

    const latLongParam = useMemo(
        () => (locationEnabled !== false ? coordinates || location?.coords : undefined),
        [coordinates, location?.coords, locationEnabled],
    );

    const prevCoordinatesRef = useRef<Coordinates | null>(null);
    const gatedCoords = useMemo(() => {
        if (!latLongParam) return undefined;
        const prev = prevCoordinatesRef.current;
        if (prev && !coordinatesChanged(prev, latLongParam)) {
            return prev;
        }
        return latLongParam;
    }, [latLongParam]);
    useEffect(() => {
        if (gatedCoords) prevCoordinatesRef.current = gatedCoords;
    }, [gatedCoords]);

    const cacheKey = `${cacheName}-0`;
    const userParams = useMemo(
        () => (userFullProfile ? getParamsFromObject(userFullProfile, dmaUserParams) : {}),
        [userFullProfile],
    );
    const mvpdId = useMemo(() => getLocalPlaybackMvpdId(userFullProfile), [userFullProfile]);

    const paramsObj = useMemo(
        () => ({
            DEBUG: 'true',
            isLTSEnabled: isLTSEnabled.toString(),
            mvpdId,
            ...userParams,
            ...(gatedCoords && {
                latitude: gatedCoords.latitude,
                longitude: gatedCoords.longitude,
            }),
        }),
        [isLTSEnabled, mvpdId, userParams, gatedCoords],
    );

    // When locationEnabled and geolocation is denied, gatedCoords stays undefined
    // and the query is permanently gated. The SSR-seeded initialDma remains authoritative.
    // Wait until the profile query has settled before enabling DMA. The anonymous fallback
    // (ANON_PROFILE) now satisfies hasRequiredDmaParams, so without this gate a logged-in
    // session would briefly request dmaInfo with anonymous params during the initial profile
    // load, before its real profile arrives. `isFetched` stays true after an error, so the
    // anonymous-fallback path (query resolves with no data) still enables DMA as intended.
    const isRequestEnabled = useMemo(
        () =>
            Boolean(
                isProfileFetched &&
                hasRequiredDmaParams(userFullProfile) &&
                (locationEnabled === false || !!gatedCoords) &&
                (options.enabled ?? true),
            ),
        [isProfileFetched, userFullProfile, locationEnabled, gatedCoords, options.enabled],
    );

    const {
        data,
        isLoading,
        isError,
        error,
        refetch: originalRefetch,
    } = useRequest({
        cacheKey,
        query,
        dataParams: { params: paramsObj as DmaParams },
        requestOptions: useMemo(
            () => ({
                staleTime: 60 * 1000,
                ...options,
                enabled: isRequestEnabled,
            }),
            [options, isRequestEnabled],
        ),
    });

    const refetch = useCallback(
        async (forceRefresh = false) => {
            // refetch() bypasses React Query's `enabled` gate, so guard against firing dmaInfo
            // with anonymous fallback params before the full profile has settled.
            if (!isProfileFetched || !hasRequiredDmaParams(userFullProfile)) {
                return [];
            }
            if (forceRefresh) {
                await queryClient.invalidateQueries({
                    queryKey: [cacheKey, { params: paramsObj as DmaParams }],
                    exact: true,
                    refetchType: 'none',
                });
            }
            const result = await originalRefetch();
            return result.data?.dmaInfo?.dmas ?? [];
        },
        [originalRefetch, queryClient, cacheKey, paramsObj, userFullProfile, isProfileFetched],
    );

    const dmas = data?.dmaInfo?.dmas || [];

    return {
        dmas,
        isLoading,
        isError,
        error,
        refetch,
        debugInfo: data?.dmaInfo?.debugInfo,
        success: data?.dmaInfo?.success,
    };
};

export default useDmas;

type SSRStateHandle = Pick<ReturnType<typeof ssrState>, 'fetchSSR'>;

type PrefetchDmaSSROptions = PrefetchLocationOptions & { locationEnabled?: boolean };

export const prefetchDmaSSR = async (
    state: SSRStateHandle,
    userFullProfile: FullUser | undefined | null,
    options?: PrefetchDmaSSROptions,
) => {
    if (!hasRequiredDmaParams(userFullProfile)) return undefined;
    const { locationEnabled = true, ...locationOptions } = options ?? {};
    if (!locationEnabled) {
        const [dmaResp] = await state.fetchSSR([prefetchDma(userFullProfile, null)]);
        return { dmaResp, coords: null };
    }
    const [locationPrefetch] = await state.fetchSSR([prefetchLocation(locationOptions)]);
    const [dmaResp] = await state.fetchSSR([
        prefetchDma(userFullProfile, locationPrefetch?.coords ?? null),
    ]);
    return { dmaResp, coords: locationPrefetch?.coords ?? null };
};

type SsrDmaResult = Awaited<ReturnType<typeof prefetchDmaSSR>>;

export function resolveSsrDma(ssrResult: SsrDmaResult, dmaEnabled: boolean): number | undefined {
    const parsedDma = Number(ssrResult?.dmaResp?.dmaInfo?.dmas?.[0]?.dma ?? 0);
    return parsedDma === 0 && dmaEnabled ? undefined : parsedDma;
}

export type SsrDmaResolution = { dma: number | undefined; stationId: string | undefined };

export const resolveSsrDmaResolution = async (
    state: SSRStateHandle,
    userFullProfile: FullUser | undefined | null,
    { query, locationEnabled, dmaEnabled }: PrefetchDmaSSROptions & { dmaEnabled: boolean },
): Promise<SsrDmaResolution> => {
    const ssrResult = await prefetchDmaSSR(state, userFullProfile, { query, locationEnabled });
    return {
        dma: resolveSsrDma(ssrResult, dmaEnabled),
        stationId: ssrResult?.dmaResp?.dmaInfo?.dmas?.[0]?.stationId ?? undefined,
    };
};

export const prefetchDma =
    (userFullProfile: FullUser, coordinates?: Coordinates | null) => (req: IncomingMessage) => {
        const userParams = getParamsFromObject(userFullProfile, dmaUserParams);
        const mvpdId = getLocalPlaybackMvpdId(userFullProfile);

        return prefetchRequest({
            cacheKey: `${cacheName}-0`,
            query,
            dataParams: {
                params: {
                    DEBUG: 'true',
                    isLTSEnabled: 'false',
                    mvpdId: mvpdId as string,
                    ...userParams,
                    ...(coordinates
                        ? { latitude: coordinates.latitude, longitude: coordinates.longitude }
                        : {}),
                } as DmaParams,
            },
            req,
        });
    };
